"use client"

import { datadogRum } from "@datadog/browser-rum"
import mixpanel from "mixpanel-browser"

type AnalyticsPrimitive = string | number | boolean | null
export type AnalyticsProps = Record<string, AnalyticsPrimitive | undefined>

type AnalyticsUser = {
  id: string
  email?: string
  role?: string | null
  name?: string
  /** Organization the user is acting under — drives per-tenant cohorts + billing rollups. */
  orgId?: string | null
  /** Team the user is acting under (within the org). */
  teamId?: string | null
  orgRole?: string | null
  teamRole?: string | null
}

/** Mixpanel group keys. Group analytics let us segment + bill by tenant. */
const ORG_GROUP_KEY = "org_id"
const TEAM_GROUP_KEY = "team_id"

let isMixpanelReady = false
let isDatadogReady = false
let pendingIdentifiedUser: AnalyticsUser | null = null

function isBrowser(): boolean {
  return typeof window !== "undefined"
}

function sanitizeProps(input?: AnalyticsProps): Record<string, AnalyticsPrimitive> {
  if (!input) return {}
  const output: Record<string, AnalyticsPrimitive> = {}
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined) continue
    output[key] = value
  }
  return output
}

function hasMixpanelToken(): boolean {
  return Boolean(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN)
}

function hasDatadogConfig(): boolean {
  return Boolean(
    process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID &&
      process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN &&
      process.env.NEXT_PUBLIC_DATADOG_SITE &&
      process.env.NEXT_PUBLIC_DATADOG_SERVICE
  )
}

function applyIdentifyToMixpanel(user: AnalyticsUser): void {
  mixpanel.identify(user.id)
  // Super-properties: attached to every subsequent event for cohorting.
  mixpanel.register(
    sanitizeProps({
      role: user.role ?? "unknown",
      org_id: user.orgId ?? undefined,
      team_id: user.teamId ?? undefined,
      org_role: user.orgRole ?? undefined,
      team_role: user.teamRole ?? undefined,
    })
  )
  mixpanel.people.set(
    sanitizeProps({
      $email: user.email,
      $name: user.name,
      role: user.role ?? "unknown",
      org_id: user.orgId ?? undefined,
      team_id: user.teamId ?? undefined,
    })
  )
  // Group Analytics — lets us analyze (and later bill) by org and team.
  if (user.orgId) {
    mixpanel.set_group(ORG_GROUP_KEY, user.orgId)
  }
  if (user.teamId) {
    mixpanel.set_group(TEAM_GROUP_KEY, user.teamId)
  }
}

function applyIdentifyToDatadog(user: AnalyticsUser): void {
  datadogRum.setUser({
    id: user.id,
    email: user.email,
    name: user.name,
    // Custom attributes — sliceable in RUM and on correlated APM traces.
    role: user.role ?? undefined,
    org_id: user.orgId ?? undefined,
    team_id: user.teamId ?? undefined,
  })
}

function flushPendingIdentify(): void {
  if (!pendingIdentifiedUser) return
  const user = pendingIdentifiedUser
  if (hasMixpanelToken() && isMixpanelReady) {
    applyIdentifyToMixpanel(user)
  }
  if (hasDatadogConfig() && isDatadogReady) {
    applyIdentifyToDatadog(user)
  }
}

export function markMixpanelReady(): void {
  isMixpanelReady = true
  flushPendingIdentify()
}

export function markDatadogReady(): void {
  isDatadogReady = true
  flushPendingIdentify()
}

export function identifyAnalyticsUser(user: AnalyticsUser): void {
  if (!isBrowser()) return
  pendingIdentifiedUser = user

  if (hasMixpanelToken() && isMixpanelReady) {
    applyIdentifyToMixpanel(user)
  }

  if (hasDatadogConfig() && isDatadogReady) {
    applyIdentifyToDatadog(user)
  }
}

export function clearAnalyticsUser(): void {
  if (!isBrowser()) return
  pendingIdentifiedUser = null

  if (hasMixpanelToken() && isMixpanelReady) {
    mixpanel.reset()
  }
  if (hasDatadogConfig() && isDatadogReady) {
    datadogRum.clearUser()
  }
}

export function trackAnalyticsEvent(event: string, props?: AnalyticsProps): void {
  if (!isBrowser()) return
  const safeProps = sanitizeProps(props)

  if (hasMixpanelToken()) {
    mixpanel.track(event, safeProps)
  }
  if (hasDatadogConfig()) {
    datadogRum.addAction(event, safeProps)
  }
}

export function trackAnalyticsError(
  event: string,
  error: unknown,
  props?: AnalyticsProps
): void {
  if (!isBrowser()) return
  const safeProps = sanitizeProps(props)
  const message =
    error instanceof Error && error.message.trim()
      ? error.message
      : "Unknown error"

  if (hasMixpanelToken()) {
    mixpanel.track(event, {
      ...safeProps,
      error_message: message,
    })
  }
  if (hasDatadogConfig()) {
    datadogRum.addError(
      error instanceof Error ? error : new Error(message),
      safeProps
    )
  }
}
